import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Upload, FileSpreadsheet, X, Loader2 } from 'lucide-react';
import { parseInventoryFile } from '../utils/excelParser';

const FileUploader = ({ onDataLoaded }) => {
    const fileInputRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleFileChange = (e) => {
        console.log('[FileUploader] onChange triggered');
        console.log('[FileUploader] e.target.files:', e.target.files);

        const file = e.target.files?.[0];
        if (file) {
            console.log('[FileUploader] File selected:', file.name, file.type, file.size);
            handleFile(file);
        } else {
            console.log('[FileUploader] No file in event');
        }
    };

    const handleFile = async (file) => {
        console.log('[FileUploader] handleFile started for:', file.name);
        setIsLoading(true);
        setError(null);

        try {
            console.log('[FileUploader] Reading file as arrayBuffer...');
            const data = await file.arrayBuffer();
            console.log('[FileUploader] ArrayBuffer size:', data.byteLength);

            console.log('[FileUploader] Parsing inventory file...');
            const jsonData = parseInventoryFile(data);
            console.log('[FileUploader] Parsed items count:', jsonData.length);

            if (jsonData.length === 0) {
                throw new Error("No valid inventory items found.");
            }

            console.log('[FileUploader] Calling onDataLoaded...');
            onDataLoaded(jsonData);
            console.log('[FileUploader] SUCCESS - Data loaded!');
        } catch (err) {
            console.error("[FileUploader] Upload error:", err);
            setError(err.message || "Failed to parse file.");
        } finally {
            setIsLoading(false);
            // Reset input to allow re-selecting the same file
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleButtonClick = () => {
        console.log('[FileUploader] Button clicked');
        console.log('[FileUploader] fileInputRef.current:', fileInputRef.current);
        if (fileInputRef.current) {
            fileInputRef.current.click();
            console.log('[FileUploader] Input click() called');
        }
    };

    const onDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const onDragLeave = () => {
        setIsDragOver(false);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    };

    return (
        <div className="relative">
            {/* Using label with nested input for maximum mobile compatibility */}
            <label
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                className={`
                    relative cursor-pointer group flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-300
                    ${isDragOver
                        ? 'bg-blue-500/20 border-blue-500 text-blue-400'
                        : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:text-white hover:border-white/30'
                    }
                `}
                style={{ WebkitTapHighlightColor: 'transparent' }} // Mobile tweak
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-50"
                    accept=".xlsx,.xls,.csv"
                    title="" /* Hides 'No file chosen' tooltip in some browsers */
                />

                {isLoading ? (
                    <Loader2 size={18} className="animate-spin" />
                ) : (
                    <Upload size={18} className="group-hover:scale-110 transition-transform" />
                )}
                <span className="text-sm font-medium">Import Data</span>
            </label>

            {error && (
                <div className="absolute top-full mt-2 right-0 w-64 p-3 bg-red-500/10 border border-red-500/50 rounded-lg backdrop-blur-xl animate-fade-in z-50">
                    <div className="flex justify-between items-start">
                        <span className="text-xs text-red-400">{error}</span>
                        <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
                            <X size={14} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FileUploader;
